import { Epic, ofType } from 'redux-observable';
import { from, Observable, of } from 'rxjs';
import { catchError, mergeMap, takeUntil } from 'rxjs/operators';

import { Action } from '../../../actions';
import {
    UpdateInternalBannerTemplateResources,
    updateInternalBannerTemplateResourcesAction
} from '../../../actions/bannersEdit/internal/updateInternalBannerTemplateResources';
import { CANCEL_UPLOAD_INTERNAL_BANNER_IMAGE } from '../../../actions/imageEditor/internal/cancelUploadInternalBannerImage';
import {
    ShowInternalBannerImageUploadError,
    showInternalBannerImageUploadErrorAction
} from '../../../actions/imageEditor/internal/showInternalBannerImageUploadError';
import {
    UpdateInternalBannerImage,
    updateInternalBannerImageAction
} from '../../../actions/imageEditor/internal/updateInternalBannerImage';
import {
    UPLOAD_INTERNAL_BANNER_IMAGE_BY_LINK,
    UploadInternalBannerImageByLink
} from '../../../actions/imageEditor/internal/uploadInternalBannerImageByLink';
import { getTextBannerImageFromUploadResponse } from '../../../mappers/banners-edit/api-data-mappers';
import { internalImageUploadErrorMapper } from '../../../service/BannerEditErrors/ErrorsTextMapper/ErrorsTextMapper';
import { ApiMethodExecutingError } from '../../../service/Errors';
import { EpicDeps } from '../../../service/StoreFactory';
import { ValidationResultItem } from '../../../typings/common';
import { AdvType, BannerImageType } from '../../../typings/domain';
import { State } from '../../../typings/state';

type EpicActions =
    | ShowInternalBannerImageUploadError
    | UpdateInternalBannerImage
    | UpdateInternalBannerTemplateResources;

export const uploadInternalBannerImageByLinkEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(UPLOAD_INTERNAL_BANNER_IMAGE_BY_LINK),
        mergeMap(
            ({ link, bannerId, resourceId }: UploadInternalBannerImageByLink): Observable<EpicActions> => {
                const { login } = store$.value.commonData;

                return from(deps.restApiManager.uploadBannerAdImageByLink(login, AdvType.INTERNAL, link)).pipe(
                    mergeMap(({ result }) => {
                        const uploadImage = getTextBannerImageFromUploadResponse(result, BannerImageType.INTERNAL);

                        return of(
                            updateInternalBannerImageAction(bannerId, resourceId, uploadImage),
                            updateInternalBannerTemplateResourcesAction(bannerId, resourceId, uploadImage.hash)
                        );
                    }),
                    takeUntil(action$.ofType(CANCEL_UPLOAD_INTERNAL_BANNER_IMAGE)),
                    catchError(
                        (error: ApiMethodExecutingError): Observable<ShowInternalBannerImageUploadError> => {
                            const errors = error.getErrors();
                            const actionErrors: ValidationResultItem[] =
                                errors.length === 0
                                    ? [
                                          {
                                              code: '',
                                              path: '',
                                              params: null
                                          }
                                      ]
                                    : errors;

                            return of(
                                showInternalBannerImageUploadErrorAction(
                                    bannerId,
                                    resourceId,
                                    actionErrors.map(err => ({
                                        code: err.code,
                                        text: internalImageUploadErrorMapper(err)
                                    }))
                                )
                            );
                        }
                    )
                );
            }
        )
    );
};
